import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import styles from "./zeroTrustPolicies.module.scss";

const defaultForm = {
  name: "",
  type: "",
  scope: "",
  description: "",
  status: "",
  enforcementMode: "",
  priority: "",
  owner: "",
};

const EditPolicyDialog = ({ open, policy, onClose, onSave }) => {
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (open && policy) {
      setForm({
        name: policy.name || "",
        type: policy.type || "",
        scope: policy.scope || "",
        description: policy.description || "",
        status: policy.status || "",
        enforcementMode: policy.enforcement || "",
        priority: policy.priority || "",
        owner: policy.owner || "",
      });
    }
  }, [open, policy]);

  const handleChange = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSave = () => {
    if (onSave) {
      onSave(form);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: styles.editDialog }}
    >
      <Box className={styles.editDialogHeader}>
        <Box>
          <Typography className={styles.editDialogTitle}>
            Edit Policy
          </Typography>
          <Typography className={styles.editDialogSubtitle}>
            Update policy configuration and settings
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Box className={styles.editDialogBody}>
        <Typography className={styles.formSectionTitle}>
          Basic Information
        </Typography>
        <Divider className={styles.formDivider} />

        <Box className={styles.formField}>
          <Typography className={styles.formLabel}>Policy Name *</Typography>
          <TextField
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            size="small"
          />
        </Box>

        <Box className={styles.formGrid}>
          <Box className={styles.formField}>
            <Typography className={styles.formLabel}>Policy Type *</Typography>
            <FormControl fullWidth size="small">
              <Select value={form.type} onChange={handleChange("type")}>
                {[
                  "Denial",
                  "Authentication",
                  "Containment",
                  "Access Control",
                ].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box className={styles.formField}>
            <Typography className={styles.formLabel}>Scope *</Typography>
            <FormControl fullWidth size="small">
              <Select value={form.scope} onChange={handleChange("scope")}>
                {[
                  "OT Network",
                  "SCADA Systems",
                  "Corporate WAN",
                  "IoT Sensors",
                ].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box className={styles.formField}>
          <Typography className={styles.formLabel}>Description</Typography>
          <TextField
            value={form.description}
            onChange={handleChange("description")}
            fullWidth
            size="small"
            multiline
            minRows={3}
          />
        </Box>

        <Typography className={styles.formSectionTitle}>
          Enforcement Settings
        </Typography>
        <Divider className={styles.formDivider} />

        <Box className={styles.formGrid}>
          <Box className={styles.formField}>
            <Typography className={styles.formLabel}>Status *</Typography>
            <FormControl fullWidth size="small">
              <Select value={form.status} onChange={handleChange("status")}>
                {["Active", "Simulation", "Testing"].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box className={styles.formField}>
            <Typography className={styles.formLabel}>
              Enforcement Mode *
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={form.enforcementMode}
                onChange={handleChange("enforcementMode")}
              >
                {["Automatic", "Manual"].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box className={styles.formField}>
          <Typography className={styles.formLabel}>Policy Owner</Typography>
          <TextField
            value={form.owner}
            onChange={handleChange("owner")}
            fullWidth
            size="small"
          />
        </Box>

        <Typography className={styles.formSectionTitle}>Preview</Typography>
        <Divider className={styles.formDivider} />
        <Box className={styles.previewCard}>
          <Box className={styles.previewRow}>
            <Typography className={styles.previewLabel}>
              Policy Name:
            </Typography>
            <Typography className={styles.previewValue}>{form.name}</Typography>
          </Box>
          <Box className={styles.previewRow}>
            <Typography className={styles.previewLabel}>Type:</Typography>
            <Chip
              size="small"
              label={form.type}
              className={styles.previewChip}
            />
          </Box>
          <Box className={styles.previewRow}>
            <Typography className={styles.previewLabel}>Status:</Typography>
            <span className={styles.previewStatusDot} />
            <Typography className={styles.previewValue}>
              {form.status}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className={styles.editDialogFooter}>
        <Button
          variant="outlined"
          className={styles.editCancel}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button className={styles.editSave} onClick={handleSave}>
          Save Changes
        </Button>
      </Box>
    </Dialog>
  );
};

export default EditPolicyDialog;
